import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Send, ChevronRight, MessageSquare, Clock } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-800 border-amber-400",
  pending: "bg-blue-100 text-asap-blue border-asap-blue",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-500",
  closed: "bg-slate-100 text-slate-600 border-slate-400",
};

export default function AdminTicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/tickets/all");
      setTickets(data);
      if (active) {
        const fresh = data.find((t) => t.id === active.id);
        if (fresh) setActive(fresh);
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (active) {
      api.get(`/tickets/${active.id}`).then((r) => setMessages(r.data.messages));
      const id = setInterval(() => api.get(`/tickets/${active.id}`).then((r) => setMessages(r.data.messages)), 4000);
      return () => clearInterval(id);
    }
  }, [active]);

  const send = async () => {
    if (!reply.trim() || !active) return;
    try {
      const { data } = await api.post(`/tickets/${active.id}/reply`, { message: reply });
      setMessages((m) => [...m, data]);
      setReply("");
    } catch {}
  };

  const setStatus = async (status) => {
    if (!active) return;
    try {
      await api.post(`/tickets/${active.id}/status`, { status });
      load();
    } catch {}
  };

  const filtered = tickets.filter((t) => filter === "all" || t.status === filter);

  return (
    <div className="bg-white border-2 border-asap-deep grid md:grid-cols-[320px_1fr] min-h-[600px]" data-testid="admin-tickets">
      <aside className="border-r-2 border-asap-deep flex flex-col">
        <div className="p-3 border-b-2 border-asap-deep flex gap-1 overflow-x-auto">
          {["all", "open", "pending", "resolved", "closed"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border-2 ${filter === f ? "border-asap-deep bg-asap-yellow" : "border-slate-200 text-slate-500"}`} data-testid={`ticket-filter-${f}`}>{f}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="admin-ticket-list">
          {filtered.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No tickets.</div>}
          {filtered.map((t) => (
            <button key={t.id} onClick={() => setActive(t)} className={`w-full text-left p-3 hover:bg-asap-yellow/10 flex items-center gap-2 ${active?.id === t.id ? "bg-asap-yellow/20" : ""}`} data-testid={`admin-ticket-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-asap-deep truncate">{t.subject}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{t.user_name} · {t.user_role}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><Clock size={10}/> {new Date(t.updated_at).toLocaleString()}</div>
              </div>
              <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_COLORS[t.status]}`}>{t.status}</span>
            </button>
          ))}
        </div>
      </aside>

      <section className="flex flex-col">
        {!active && <div className="flex-1 flex items-center justify-center text-slate-400">Select a ticket to view & reply.</div>}
        {active && (
          <>
            <header className="p-4 border-b-2 border-asap-deep">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display text-xl uppercase text-asap-deep">{active.subject}</div>
                  <div className="text-xs text-slate-500 mt-1">From {active.user_name} ({active.user_role}) · {new Date(active.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setStatus("pending")} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border-2 border-asap-blue text-asap-blue hover:bg-asap-blue hover:text-white" data-testid="ticket-set-pending">Pending</button>
                  <button onClick={() => setStatus("resolved")} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-white" data-testid="ticket-set-resolved">Resolved</button>
                  <button onClick={() => setStatus("closed")} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border-2 border-slate-400 text-slate-600 hover:bg-slate-400 hover:text-white" data-testid="ticket-set-closed">Closed</button>
                </div>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-3" data-testid="admin-ticket-thread">
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[80%] ${m.author_role === "admin" ? "ml-auto" : ""}`}>
                  <div className={`p-3 rounded ${m.author_role === "admin" ? "bg-asap-deep text-white" : "bg-white border-2 border-asap-deep"}`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{m.author_name} · {m.author_role}</div>
                    <div className="mt-1 text-sm whitespace-pre-wrap">{m.message}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t-2 border-asap-deep flex gap-2 bg-white">
              <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a reply…" className="flex-1 px-3 py-2 border-2 border-asap-deep outline-none" data-testid="admin-reply-input"/>
              <button onClick={send} className="px-4 py-2 bg-asap-yellow text-asap-deep border-2 border-asap-deep" data-testid="admin-reply-send"><Send size={16}/></button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
